import { type ReactElement } from 'react';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { ResultsFileDetailsPanels } from '@/features/uploads/components/ResultsFileDetailsPanels';
import { ResultsFileStatusRows } from '@/features/uploads/components/ResultsFileStatusRows';
import { ResultsFileSuiteExecutionPanelsSection } from '@/features/uploads/components/ResultsFileSuiteExecutionPanelsSection';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type {
  BuildDto,
  ExecutionRunDto,
  ResultsFileDto,
  SuiteExecutionDto,
  UploadDto,
} from '@flowqa/shared';

export type ResultsFileDetailContentProps = {
  title: string;
  resultsFile: ResultsFileDto;
  build: BuildDto | null;
  upload: UploadDto | null;
  executionRun: ExecutionRunDto | null;
  suiteExecutions: SuiteExecutionDto[];
  suiteExecutionsLoading?: boolean;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const ResultsFileDetailContent = ({
  title,
  resultsFile,
  build,
  upload,
  executionRun,
  suiteExecutions,
  suiteExecutionsLoading = false,
  onDelete,
  deleteLoading = false,
}: ResultsFileDetailContentProps): ReactElement => {
  const executionRunId = upload?.latestExecutionRunId ?? upload?.executionRunIds[0] ?? null;
  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Results File" />
          {title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Results"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deletion will remove all associated execution runs, suite executions, and test
                executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Results"
        />
      }
      metadata={
        <ResultsFileDetailsPanels resultsFile={resultsFile} build={build} layout="columns" />
      }
      summary={<ResultsFileStatusRows resultsFile={resultsFile} />}
      primaryContent={
        <ResultsFileSuiteExecutionPanelsSection
          suiteExecutions={suiteExecutions}
          loading={suiteExecutionsLoading}
          executionRunId={executionRunId}
          executionRun={executionRun}
        />
      }
    />
  );
};
